/**
 * A filter which applies itself as it is typed.
 *
 * A search which needs a button pressed is a search somebody types into,
 * looks at, and then wonders why nothing happened. The form here sends
 * itself: a moment after the last keystroke for a box, and at once for
 * anything which is switched rather than written, because a switch is
 * already a decision.
 *
 * The button is not removed, it is hidden. Without scripts the form is an
 * ordinary one and the button is the way to send it, which is what every
 * other listing of the administration does.
 *
 * Where the caret was is carried across the reload. The answer arrives as a
 * new page, and a page which puts the caret back at the start of the box is
 * a page nobody can type a second word into.
 */

import { query, queryAll } from "../core/dom";

/** How long the typing has to stop before the filter is sent. */
export const TYPING_PAUSE = 400;

/** Where the name of the field being typed into is remembered. */
export const FOCUS_KEY = "techcraft.search.focus";

/** Return whether `control` is written into rather than switched. */
function isTyped(control: Element): boolean {
    return control instanceof HTMLInputElement && (control.type === "search" || control.type === "text");
}

/** Remember which field is being typed into, and send the form. */
function send(form: HTMLFormElement, focused: HTMLElement | null): void {
    try {
        const name = focused instanceof HTMLInputElement ? focused.name : "";
        window.sessionStorage.setItem(FOCUS_KEY, name);
    } catch {
        // Remembering the caret is a courtesy, never a requirement.
    }

    form.submit();
}

/** Put the caret back into the field the last page was typed into. */
function restoreFocus(form: HTMLFormElement): void {
    let name = "";

    try {
        name = window.sessionStorage.getItem(FOCUS_KEY) ?? "";
        window.sessionStorage.removeItem(FOCUS_KEY);
    } catch {
        return;
    }

    if (!name) {
        return;
    }

    const field = query<HTMLInputElement>(`input[name="${name}"]`, form);
    if (field) {
        field.focus();
        field.setSelectionRange(field.value.length, field.value.length);
    }
}

/** Wire one form which filters a listing. */
export function bindLiveSearch(form: HTMLFormElement): void {
    let timer: ReturnType<typeof setTimeout> | undefined;

    for (const button of queryAll<HTMLElement>("[data-live-search-submit]", form)) {
        button.hidden = true;
    }

    form.addEventListener("input", (event: Event) => {
        const control = event.target;
        if (!(control instanceof Element) || !isTyped(control)) {
            return;
        }

        if (timer !== undefined) {
            clearTimeout(timer);
        }

        timer = setTimeout(() => send(form, control as HTMLElement), TYPING_PAUSE);
    });

    // A switch is a decision rather than a thought in progress, so it is not
    // waited on - and any typing waiting to be sent goes with it.
    form.addEventListener("change", (event: Event) => {
        const control = event.target;
        if (!(control instanceof Element) || isTyped(control)) {
            return;
        }

        if (timer !== undefined) {
            clearTimeout(timer);
        }

        send(form, null);
    });

    restoreFocus(form);
}

/** Wire every filtering form of the page. */
export function initLiveSearch(): void {
    for (const form of queryAll<HTMLFormElement>("[data-live-search]")) {
        bindLiveSearch(form);
    }
}
